import { ApiService } from "./api.service";
import {
  ProductModel,
  CreateProductModel,
  ProductTypesModel,
} from "../models/products/product.model";
import { IResponse } from "../models/response";
import { useQuery, useMutation, MutationFunction } from "@tanstack/react-query";
import { AxiosError } from "axios";

/**
 * Hook to fetch a paginated list of products.
 *
 * @param {number|null} [per_page=null] - Number of items per page.
 * @param {number} [page=1] - Page number for pagination.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting options.
 * @returns {Object} A React Query object with products list and a `refetch` function.
 */
export const useFetchProducts = (
  query: string = "",  //add query to search
  per_page: number | null = null,
  page = 1,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = ["products", query, per_page, page, filters, sort];

  const { refetch, ...queryResult } = useQuery<
    IResponse<ProductModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params: Record<string, unknown> = {
      query: query || "",
      page: page,
      per_page: per_page,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };

    const productsData = await ApiService.get(`/products`, { params });
    return productsData?.data;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch all products without pagination.
 *
 * @returns {Object} A React Query object with all products and a `refetch` function.
 */
export const useFetchProductsWithoutPagination = () => {
  const queryKey = ["products"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<ProductModel[]>,
    AxiosError
  >(queryKey, () => ApiService.get(`/products?pagination=0`));
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch the details of a specific product by ID.
 *
 * @param {string} [id] - Product ID.
 * @returns {Object} A React Query object with product details and a `refetch` function.
 */
export const useFetchProduct = (id?: string) => {
  const queryKey = ["products", id];
  const { refetch, ...querResult } = useQuery<
    IResponse<ProductModel>,
    AxiosError
  >(
    queryKey,
    async () => {
      const productsData = await ApiService.get(`/products/${id}`);
      return productsData;
    },
    {
      staleTime: 60 * 1000,
      enabled: Boolean(id),
    }
  );
  return { ...querResult, refetch };
};

/**
 * Hook to fetch available product types.
 *
 * @returns {Object} A React Query object with product types and a `refetch` function.
 */
export const useFetchProductTypes = () => {
  const queryKey = ["product-types"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<ProductTypesModel[]>,
    AxiosError
  >(queryKey, async () => {
    const productTypes = await ApiService.get(`/product-types`);
    return productTypes;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to create a new product.
 *
 * @returns {Object} A React Query mutation object to create a new product using FormData.
 */
export const useCreateProduct = () => {
  const createProduct: MutationFunction<
    IResponse<CreateProductModel>,
    FormData
  > = async (formData) => {
    const response = await ApiService.postFormData(`/products`, formData);
    return response.data;
  };
  return useMutation(createProduct);
};

/**
 * Hook to update an existing product.
 *
 * @param {string} [productId] - Product ID.
 * @returns {Object} A React Query mutation object to update a product using FormData.
 */
export const useUpdateProduct = (productId?: string) => {
  const updateProduct: MutationFunction<
    IResponse<CreateProductModel>,
    FormData
  > = async (formData) => {
    const response = await ApiService.postFormData(
      `/products/${productId}?_method=PATCH`,
      formData
    );
    return response;
  };
  return useMutation(updateProduct);
};
